import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchBox } from "../../../components/Search";
import {
  CompareRajasthan,
  RajasthanCollective,
  RajasthanDiscomData,
  RajasthanGenData,
  RajasthanGnaTgna,
  RajasthanIsgs,
  RajBankingData,
  ReEnergyData,
} from "../../../models/rajasthan";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import {
  getRajasthanIsgsData,
  getRajasthanCollectiveData,
  getGnaTgnaData,
  getRajasthanGenerationData,
  getRajasthanDiscomData,
  getRajReData,
  getRajTamData,
  getRajDeep,
  getRajBankingData,
} from "../Utility/RestApi";
import {
  ChartData,
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { BrushStart, ChartType, LegendKey } from "../../../models/chart_model";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { FilterTab } from "../../../components/Filters";
import { titleCase } from "../../../extensions/extension";
import { stringToConsistentColor } from "../../../extensions/string";
import Loading from "../../../components/Loading";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

interface RajData {
  discomLoad: RajasthanDiscomData[];
  collective: RajasthanCollective[];
  compare: CompareRajasthan[];
  isgs: RajasthanIsgs[];
  gnaTgna: RajasthanGnaTgna[];
  reGen: ReEnergyData[];
  intraGen: RajasthanGenData[];
  bankingData: RajBankingData[];
}
const KEYS: {
  name: string;
  firstKey: string;
  secondKey: string | undefined;
  isTime: boolean;
}[] = [
  {
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    isTime: true,
  },
  { name: "Date", firstKey: "date", secondKey: undefined, isTime: false },
  // { name: "Month", firstKey: "month", secondKey: undefined },
];
const UNITS = ["MW", "MWh"];

const DISCOM_LEGENDS = [
  // Ajmer Discom, Jaipur Discom, Jodhpur Discom
  {
    key: "Ajmer Discom",
    label: "Ajmer Discom",
    stackId: "discom",

    color: ExchangeColors[1],
  },
  {
    key: "Jaipur Discom",
    label: "Jaipur Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
  {
    key: "Jodhpur Discom",
    label: "Jodhpur Discom",
    stackId: "discom",

    color: ExchangeColors[3],
  },
];

const TOTAL_DISCOM_LEGENDS = [
  {
    key: "Total Discom",
    label: "Load",
    color: ExchangeColors[1],
    stackId: "discom",
  },
];
const SHORT_TERM_LEGENDS = [
  {
    key: "OA_GNA Buy",
    label: "OA GNA",
    color: ExchangeColors[8],
    stackId: "generation",
  },
  {
    key: "OA_TGNA Buy",
    label: "OA TGNA",
    color: ExchangeColors[7],
    stackId: "generation",
  },
  {
    key: "Collective Buy",
    label: "Collect.",
    color: ExchangeColors[4],
    stackId: "generation",
  },
];
const CONSUMPTION = [
  {
    label: "Total",
    value: TOTAL_DISCOM_LEGENDS,
  },
  {
    label: "By Discom",
    value: DISCOM_LEGENDS,
  },
];
export function OverviewDas({
  dates,
}: {
  dates: { startDate: Date; endDate: Date };
}) {
  const [data, setData] = useState<RajData>({
    discomLoad: [],
    collective: [],
    compare: [],
    isgs: [],
    gnaTgna: [],
    reGen: [],
    intraGen: [],
    bankingData: [],
  });

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    _handleFetchData();
  }, [dates]);

  async function _handleFetchData() {
    const startDateStr = convertToString(dates.startDate);
    const endDateStr = convertToString(dates.endDate);
    setLoading(true);
    // Initiate all API calls concurrently
    const [
      isgsData,
      collectiveData,
      gnaTgnaData,
      generationData,
      discomData,
      reGenData,
      tamData,
      deepData,
      bankingData,
    ] = await Promise.all([
      getRajasthanIsgsData({
        startDate: startDateStr,
        endDate: endDateStr,
      }),
      getRajasthanCollectiveData({
        startDate: startDateStr,
        endDate: endDateStr,
      }),
      getGnaTgnaData({ startDate: startDateStr, endDate: endDateStr }),
      getRajasthanGenerationData(startDateStr, endDateStr),
      getRajasthanDiscomData(startDateStr, endDateStr),
      getRajReData({ startDate: startDateStr, endDate: endDateStr }),
      getRajTamData({ startDate: startDateStr, endDate: endDateStr }),
      getRajDeep({ startDate: startDateStr, endDate: endDateStr }),
      getRajBankingData({ startDate: startDateStr, endDate: endDateStr }),
    ]);

    const processedIsgsData = isgsData
      .map((item) => ({
        ...item,
        value_mwh: item.value_mw / 4,
        entity: item.from_utility__name + " " + "ISGS Station",
        type: "ISGS",
        time_block: item.slot,
        month: getMonthFromDate(item.date),
      }))
      .filter((item) => item.value_mw > 0);

    const processedCollectiveData = collectiveData.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: isBuy ? "Collective Buy" : "Collective Sell",
        time_block: item.time_slot,
        exchange: item.exchange__name,
        product: item.product__name,
        price_kwh: item.wt_mcp_rs_kwh,
        entity: item.exchange__name + " " + item.product__name,
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
      };
    });
    const processedGnaTgnaData = gnaTgnaData
      .map((item) => {
        const isBuy = item.value_mw > 0;
        return {
          ...item,
          type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
          entity: item.from_utility__name,
          toEntity: item.to_utility__name,
          value_mwh: Math.abs(item.value_mw) / 4,
          value_mw: Math.abs(item.value_mw),
          month: getMonthFromDate(item.date),
        };
      })
      .filter((item) => item.value_mw > 0);
    const processedGenerationData = generationData
      .map((item) => ({
        ...item,
        type: "Intra Generation",
        value_mwh: item.onBarDS_mw! / 4,
        entity: item.station + " " + "Intra Generator",
        value_mw: item.onBarDS_mw!,
        time_block: item.slot,
        month: getMonthFromDate(item.date),
      }))
      .filter((item) => item.value_mw > 0);

    const processedReData = reGenData.map((item) => ({
      ...item,
      type: item.area + " RE Generation",
      value_mwh: item.value_mw / 4,
      entity: item.energy_type + " RE",
    }));

    const processedTamData = tamData.map((item) => ({
      ...item,
      type: "TAM",
      exchange: item.exchange,
      product: item.product,
      value_mwh: item.mw / 4,
      value_mw: item.mw,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    const processedDeepData: CompareRajasthan[] = deepData
      .map((item) => {
        return {
          ...item,
          type: `Deep`,
          exchange: "Deep",
          product: "RA",
          entity: item.entity,
          time_block: item.slot,
          toEntity: item.entity,
          value_mwh: Math.abs(item.value_mw) / 4,
          value_mw: Math.abs(item.value_mw),
          month: getMonthFromDate(item.date),
        };
      })
      .filter((item) => item.value_mw > 0);

    const processedBankingData = bankingData.map((item) => ({
      ...item,
      type: "Banking",
      exchange: "Banking",
      product: "Banking",
      value_mwh: item.mw / 4,
      value_mw: item.mw,
      time_block: item.time_block,
      month: getMonthFromDate(item.date),
    }));

    // Combine all processed data
    const newData: CompareRajasthan[] = [
      ...processedIsgsData,
      ...processedCollectiveData,
      ...processedGnaTgnaData,
      ...processedGenerationData,
      ...processedReData,
      ...processedTamData,
      ...processedDeepData,
      ...processedBankingData,
    ];

    setData({
      discomLoad: discomData,
      collective: collectiveData,
      compare: newData,
      isgs: isgsData,
      gnaTgna: gnaTgnaData,
      reGen: reGenData,
      intraGen: generationData,
      bankingData: bankingData,
    });
    setLoading(false);
  }
  return (
    <div className="tabContent">
      <div
        style={{ height: "calc(100vh - 90px)" }}
        className="grid grid-cols-12 gap-4 w-full px-4  p-4 md:overflow-hidden"
      >
        {loading && <Loading />}
        <div className="dash-card col-span-1 md:col-span-6">
          <SummaryChart data={data.compare} discomData={data.discomLoad} />
          {/* VolPriceChart Component Placeholder */}
        </div>
        {/* Top Right */}
        <div className="dash-card col-span-1 md:col-span-6">
          <ProcurementChart data={data.compare} />
          {/* GenerationSplit Component Placeholder */}
        </div>
        {/* Bottom Left */}
        <div className="dash-card col-span-1 md:col-span-6">
          <GenerationSplit
            intraGen={data.intraGen}
            reGen={data.reGen}
            interGen={data.isgs}
          />
        </div>
        {/* Bottom Center */}
        <div className="dash-card col-span-1 md:col-span-6">
          <DiscomLoad
            data={data.discomLoad.map((item) => ({
              ...item,
            }))}
          />
        </div>
        {/* Bottom Right */}
        {/* <div className="flex justify-between col-span-1 md:col-span-12">
          <p className="gna-footer-text">Source - IGX</p>
        </div> */}
      </div>{" "}
      <div className="gna-footer-container">
        <div className="grid grid-cols-3 justify-between gap-5 ">
          <Tooltip
            note={<p>RUVITL, Eltrix, WBES</p>}
            text=" Short Term Procurement % = (Collective + TGNA)/Total Discom Load   (2).
            Intrastate Generation = Eltrix schedule + NREB Grid Curves   (3).
            Interstate Generation = ISGS WBES   (4). MW = Represents the average
            (MW) when the selected time interval is Date or Month."
          >
            <p className="gna-footer-text">Source</p>
          </Tooltip>
          <p className="gna-footer-text col-span-1 text-center">
            Powered by <strong>GNA Energy</strong>
          </p>
          <strong className="gna-footer-text col-span-1 text-end">
            Beta Version
          </strong>
        </div>
      </div>
    </div>
  );
}

function SummaryChart({
  data,
  discomData,
}: {
  data: CompareRajasthan[];
  discomData: RajasthanDiscomData[];
}) {
  const [chartData, setChartData] = useState<ChartData>();
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectedConsumption, setSelectedConsumption] = useState(
    CONSUMPTION[0]
  );
  const getSelectedUnit = useCallback(
    () => (selectedKey.isTime ? UNITS[0] : UNITS[1]),
    [selectedKey]
  );

  const uniqueEntities = Array.from(
    new Set(data.map((item) => [item.entity, item.toEntity]).flat())
  ).filter((item) => item !== undefined) as string[];

  const LEGENDS = [
    {
      key: "ISGS",
      label: "ISGS Gen.",
      color: ExchangeColors[3],
      stackId: "generation",
    },

    {
      key: "Intra Generation",
      label: "Intra Gen.",
      color: ExchangeColors[0],
      stackId: "generation",
    },
    {
      key: "IntraState RE Generation",
      label: "Intra RE Gen.",
      color: "#00FF00",
      stackId: "generation",
    },
    {
      key: "Inter RE Gen.",
      label: "OA RE Buy",
      color: "green",
      stackId: "generation",
    },
  ];

  useEffect(() => {
    const processedDiscomData = discomData.map((item) => ({
      ...item,
      type: "Total Discom",
      value_mwh: (item.drawal_mw + item.load_shedding_mw) / 4,
      value_mw: item.drawal_mw + item.load_shedding_mw,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    let filteredData = data.concat(processedDiscomData);
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!)
      );
    }

    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      groupBy: ["type"],
      volumeKey: "value_mwh",
      reverseSort: ["date", "time_block"],
      aggregationKeys: ["value_mw"],
      priceKey: "",
      data: filteredData,
    };

    const res = FormatChartData(par);

    // Pre-calculate keys for faster access during map operation
    const collectiveBuyKey = `Collective Buy_value_${getSelectedUnit().toLowerCase()}`;
    const oaTgnaBuyKey = `OA_TGNA Buy_value_${getSelectedUnit().toLowerCase()}`;

    // Create a lookup map for exchange/product info to avoid filtering in every iteration
    const exchangeInfoMap = new Map();
    data.forEach((dataItem) => {
      if (
        dataItem.type === "Collective Buy" ||
        dataItem.type === "Collective Sell"
      ) {
        const key = `${dataItem.date}_${dataItem.time_block}`;
        if (!exchangeInfoMap.has(key)) {
          exchangeInfoMap.set(key, []);
        }
        exchangeInfoMap.get(key).push(dataItem);
      }
    });

    res.data = res.data.map((item) => {
      // Calculate total consumption
      let total = 0;
      for (const column of selectedConsumption.value) {
        const key = column.key + "_value_" + getSelectedUnit().toLowerCase();
        total += item[key] || 0;
      }

      // Calculate short term directly using pre-calculated keys
      const shortTerm =
        (item[collectiveBuyKey] || 0) + (item[oaTgnaBuyKey] || 0);
      const percent = total > 0 ? (shortTerm / total) * 100 : 0;

      // Get exchange data using lookup map instead of filtering
      const key = `${item.date}_${item.time_slot}`;
      const dataItems = exchangeInfoMap.get(key) || [];

      // Find specific exchange products
      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM"
      );
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM"
      );
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "GDAM"
      );
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" && dataItem.product__name === "RTM"
      );

      return {
        ...item,
        shortTerm: Number(percent.toFixed(2)) > 0 ? percent.toFixed(2) : 0,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });

    setChartData(res);
  }, [
    data,
    selectedKey,
    selectedEntity,
    discomData,
    selectedConsumption.value,
    getSelectedUnit,
  ]);

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-2">
        <h2 className="chartHeading">Summary</h2>

        <div className="flex justify-end gap-3 items-center">
          {" "}
          <SearchBox
            options={uniqueEntities.map((item) => ({
              label: item,
              value: item,
            }))}
            isMany={true}
            placeholder="Search Entities"
            selectedOptions={selectedEntity.map((item) => ({
              label: item,
              value: item,
            }))}
            onChange={(item: any) => {
              setSelectedEntity(item.map((item: any) => item.value));
            }}
          />
          <FilterTab
            options={KEYS.map((item) => item.name)}
            selected={[selectedKey.name]}
            onClick={(item, index) => {
              handleKeyChange(index);
            }}
          />
        </div>
      </div>

      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.firstKey}
        xTick={
          selectedKey.secondKey !== undefined
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        secondXDataKey={selectedKey.secondKey}
        unit={getSelectedUnit()}
        isTimeSlot={selectedKey.firstKey === "time_block"}
        onlyTitle={true}
        secondYAxisLabel="%"
        xaxisHeight={selectedKey.secondKey ? 40 : 20}
        yAxisLabel={getSelectedUnit()}
        showBrush={getShowBrush(chartData?.data || [])}
        brushIndex={getBrushIndex()}
        legends={getActiveLegends()}
      />
    </div>
  );
  function getActiveLegends() {
    const activityLegends = selectedConsumption.value
      .concat(LEGENDS)
      .concat(SHORT_TERM_LEGENDS);

    return activityLegends
      .map((legend) => {
        return {
          dataKey: legend.key + "_value_" + getSelectedUnit().toLowerCase(),
          name: legend.label,
          stroke: legend.color,
          stackId: legend.stackId,
          type: ChartType.Bar,
        };
      })
      .concat([
        {
          dataKey: "shortTerm",
          name: "Short Term Proc. %",
          yAxisId: "right",
          stroke: lightenColor(ExchangeColors[2], 20),
          stackId: "generation",
          type: ChartType.Line,
        } as any,
      ]) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 8;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 8 };
    }
  }
}
function ProcurementChart({ data }: { data: CompareRajasthan[] }) {
  const [chartData, setChartData] = useState<ChartData>();
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);

  const getSelectedUnit = () => (selectedKey.isTime ? UNITS[0] : UNITS[1]);

  const uniqueEntities = Array.from(
    new Set(data.map((item) => [item.entity, item.toEntity]).flat())
  ).filter((item) => item !== undefined) as string[];

  useEffect(() => {
    let filteredData = data;
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!)
      );
    }
    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      groupBy: ["exchange", "product"],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mw"],
      priceKey: "price_kwh",
      data: filteredData,
    };

    const res = FormatChartData(par);
    console.log("useEffect ~ res:", res.data);
    setChartData(res);
  }, [data, selectedKey, selectedEntity]);
  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-2">
        <h2 className="chartHeading">Short Term Procurement</h2>
        <FilterTab
          options={KEYS.map((item) => item.name)}
          selected={[selectedKey.name]}
          onClick={(item, index) => {
            handleKeyChange(index);
          }}
        />
        {/* <div className="min-w-40">
          <SearchBox
            options={uniqueEntities.map((item) => ({
              label: item,
              value: item,
            }))}
            isMany={true}
            placeholder="Search Entities"
            selectedOptions={selectedEntity.map((item) => ({
              label: item,
              value: item,
            }))}
            onChange={(item: any) => {
              setSelectedEntity(item.map((item: any) => item.value));
            }}
          />
        </div> */}
      </div>

      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.firstKey}
        xTick={
          selectedKey.secondKey !== undefined
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        secondXDataKey={selectedKey.secondKey}
        unit={getSelectedUnit()}
        isTimeSlot={selectedKey.firstKey === "time_block"}
        onlyTitle={true}
        secondYAxisLabel="Rs/kWh"
        xaxisHeight={selectedKey.secondKey ? 40 : 20}
        yAxisLabel={getSelectedUnit()}
        showBrush={getShowBrush(chartData?.data || [])}
        brushIndex={getBrushIndex()}
        legends={getActiveLegends()}
      />
    </div>
  );
  function getActiveLegends() {
    const activityLegends = [
      {
        key: "IEX_RTM",
        label: "IEX RTM",
        color: ExchangeColors[1],
        stackId: "generation",
      },
      {
        key: "IEX_DAM",
        label: "IEX DAM",
        color: ExchangeColors[0],
        stackId: "generation",
      },
      {
        key: "IEX_GDAM",
        label: "IEX GDAM",
        color: ExchangeColors[4],
        stackId: "generation",
      },
      {
        key: "PXIL_RTM",
        label: "PXIL RTM",
        color: "purple",
        stackId: "generation",
      },
      {
        key: "PXIL_TAM",
        label: "PXIL TAM",
        color: ExchangeColors[2],
        stackId: "generation",
      },

      {
        key: "IEX_TAM",
        label: "IEX TAM",
        color: "blue",
        stackId: "generation",
      },

      {
        key: "HPX_TAM",
        label: "HPX TAM",
        color: "green",
        stackId: "generation",
      },
      { key: "IEX_LDC", label: "IEX LDC", color: "red", stackId: "generation" },
      {
        key: "PXIL_LDC",
        label: "PXIL LDC",
        color: "orange",
        stackId: "generation",
      },
      {
        key: "HPX_LDC",
        label: "HPX LDC",
        color: "brown",
        stackId: "generation",
      },

      {
        key: "Banking_Banking",
        label: "Banking",
        color: "brown",
        stackId: "generation",
      },

      {
        key: "Deep_RA",
        label: "DEEP RA",
        color: "blue",
        stackId: "generation",
      },
    ];

    return activityLegends
      .map((legend) => {
        return {
          dataKey: legend.key + "_value_" + getSelectedUnit().toLowerCase(),
          name: legend.label,
          stroke: legend.color,
          stackId: "pr",
          type: ChartType.Bar,
        };
      })
      .concat(
        activityLegends.map((legend) => {
          return {
            dataKey: legend.key + "_price_kwh",
            name: legend.label + " Price",
            stroke: legend.color.includes("#")
              ? lightenColor(legend.color, 0.7)
              : legend.color,
            type: ChartType.Line,
            stackId: "pr",
            yAxisId: "right",
          };
        })
      ) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 8;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 8 };
    }
  }
}

function GenerationSplit({
  interGen,
  intraGen,
  reGen,
}: {
  interGen: RajasthanIsgs[];
  reGen: ReEnergyData[];
  intraGen: RajasthanGenData[];
}) {
  const keys = [
    { name: "Area", key: "area", groupBy: ["area"] },
    { name: "Station", key: "station", groupBy: ["station"] },
    { name: "Source", key: "source", groupBy: ["source", "area"] },
  ];

  const units = [
    { name: "MWh", key: "value_mwh" },
    { name: "Percent", key: "percent" },
  ];

  const [selectedKey, setSelectedKey] = useState(keys[2]);
  const [selectedUn, setSelectedUn] = useState(units[0]);
  const [chartData, setChartData] = useState<ChartData>();

  // Memoize the transformed data to prevent recreation on every render
  const data = useMemo(() => {
    const re = reGen
      .filter((i) => i.energy_type !== "")
      .map((item) => ({
        ...item,
        area: item.area == "InterState" ? "Inter State" : "Intra State",
        station: "RE " + item.area,
        source: titleCase(item.energy_type),
        slot: item.time_block,
      }));

    const intra = intraGen.map((item) => ({
      ...item,
      area: "Intra State",
      station: item.station,
      source: titleCase(item.source),
      slot: item.slot,
    }));

    const inter = interGen.map((item) => ({
      ...item,
      area: "Inter State",
      station: item.station,
      source: titleCase(item.from_gen__generation_source),
      slot: item.slot,
    }));

    return intra.concat(re as any).concat(inter as any);
  }, [intraGen, interGen, reGen]);

  useEffect(() => {
    const par: ChartDataParams = {
      keys: [selectedKey.key],
      groupBy: [],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mwh"],
      priceKey: "",
      data: data,
    };

    const res = FormatChartData(par);

    // Skip processing if data is empty or invalid
    if (!res.data.length) {
      setChartData(res);
      return;
    }

    const total = res.data.reduce((acc, item) => {
      return acc + (item["_value_mwh"] || 0);
    }, 0);

    res.data = res.data
      .map((item) => {
        const key_percent = {
          ...res.keys.reduce((acc, column) => {
            acc[column + "_percent"] =
              total > 0 ? (item[column + "_value_mwh"] / total) * 100 : 0;
            return acc;
          }, {} as Record<string, any>),
        };
        return {
          ...item,
          ...key_percent,
        };
      })
      .sort((a, b) => b["_value_mwh"] - a["_value_mwh"]);

    setChartData(res);
  }, [selectedKey, data]); // Only depend on selectedKey and memoized data

  // Rest of the component remains unchanged...
  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-2">
        <h2 className="chartHeading">Generation Split</h2>
        <FilterTab
          options={keys.map((item) => item.name)}
          selected={[selectedKey.name]}
          onClick={(item, index) => {
            setSelectedKey(keys[index]);
          }}
        />
      </div>
      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.key}
        unit="MWh"
        secondYAxisLabel="Generation %"
        isTimeSlot={false}
        onlyTitle={true}
        barCategoryGap={12}
        secondYAxisWidth={40}
        brushIndex={{ startIndex: 0, endIndex: 7 }}
        yAxisWidth={65}
        xaxisHeight={(chartData?.data || []).length > 10 ? 30 : 20}
        yAxisLabel="MWh"
        showBrush={(chartData?.data || []).length > 10}
        legends={[
          {
            dataKey: "_value_mwh",
            name: "Generation MWh",
            stroke: ExchangeColors[0],
            stackId: "generation",
            type: ChartType.Bar,
          },
          {
            dataKey: "_percent",
            name: "Generation %",
            stroke: ExchangeColors[1],
            stackId: "generation",
            yAxisId: "right",
            type: ChartType.Line,
          },
        ]}
      />
    </div>
  );
}

function DiscomLoad({ data }: { data: RajasthanDiscomData[] }) {
  const X_AXIS = [
    {
      label: "Time Slot",
      key: "time",
      values: ["block", "date"],
      isTime: true,
    },
    {
      label: "Date",
      key: "date",
      values: ["date"],
      isTime: false,
    },
  ];
  const [chartData, setChartData] = useState<ChartData>({
    data: [],
    keys: [],
  } as any);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  console.log("🚀 ~ DiscomLoad ~ selectedInterval:", selectedInterval);

  const chartSuffixes = [
    { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
    { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}_` + (selectedXaxis.isTime ? "mw" : "mwh"),
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: undefined,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index], 25)
          : suffix == "drawal"
          ? ExchangeColors[index]
          : stringToConsistentColor(`${key}_${suffix}`),
      type: ChartType.Bar,
      stackId: key.split("_")[1],
    };
  };
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);
  useEffect(() => {
    let filteredData = data;
    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }
    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: ["discom"],
      volumeKey: "",
      aggregationKeys: [
        "schedule_mw",
        "drawal_mw",
        "schedule_mwh",
        "drawal_mwh",
      ],
      averageKeys: ["schedule_mw", "drawal_mw"],
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData);
  }, [data, selectedInterval]);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-2">
        <h2 className="chartHeading">Discom Load</h2>
        <FilterTab
          options={X_AXIS.map((item) => item.label)}
          selected={[selectedInterval.label]}
          onClick={(item, index) => {
            setSelectedInterval(X_AXIS[index]);
          }}
        />
      </div>
      <ReMixChart
        data={chartData?.data || []}
        xTick={
          selectedInterval.isTime
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        showBrush={chartData?.data.length > 96 ? true : undefined}
        brushStart={BrushStart.Start}
        isTimeSlot={selectedInterval.isTime}
        onlyTitle={!selectedInterval.isTime}
        yAxisWidth={65}
        secondYAxisWidth={0}
        xaxisHeight={selectedInterval.isTime ? 40 : 20}
        unit={selectedInterval.isTime ? "MW" : "MWh"}
        yAxisLabel={selectedInterval.isTime ? "MW" : "MWh"}
        legends={selectedSuffix.flatMap(
          ({ suffix, displayName, stackSuffix }) =>
            chartData.keys.map(
              (key, index) =>
                createChartObject(
                  key,
                  index,
                  suffix,
                  displayName,
                  stackSuffix,
                  selectedInterval,
                  ExchangeColors
                ) as any
            )
        )}
        xDataKey={selectedInterval.values[0]}
        secondXDataKey={
          selectedInterval.isTime ? selectedInterval.values[1] : undefined
        }
      />
    </div>
  );
}
